import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import AddEditSection from './advancedPackComponents/AddEditSection';
import ProductProp from '../../propTypes/productProp';
import { useModal } from '../../../../services/customHooks';
import AddPackProduct from './advancedPackComponents/AddPackProduct';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import PackVariation from './PackVariation';
import ShopProductVariationQuantity from '../ShopProductVariationQuantity';
import translate from '../../../../commonComponents/translate';
import ProductVariationsProp from '../../propTypes/productVariationsProp';

function HybridPackSection(props) {
  const { t, product, packQuantity, onPackQuantityChange } = props;

  const { multiPackVariationsAvailable = [] } = product;

  return (
    <section>
      {multiPackVariationsAvailable.map((packVariation, index) => (
        <Section
          // eslint-disable-next-line react/no-array-index-key
          key={`${packVariation._id}-${packVariation.name}-${index}`}
          {...props}
          {...packVariation}
          sectionId={packVariation._id}
        />
      ))}

      <div className="product-info-item variation">
        <div className="product-info-wrapper">
          <div className="pack-quantity-wrapper">
            <p className="pack-quantity">{t('shop.pack.quantity')}</p>
            <ShopProductVariationQuantity
              index={1}
              handleChange={onPackQuantityChange}
              variation={product}
              currentVariation={{ ...product, amount: packQuantity }}
              isTouched
              showLabel={false}
              hideMinusButtonThreshold={1}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

HybridPackSection.propTypes = {
  t: PropTypes.func.isRequired,
  product: PropTypes.shape(ProductProp.propType).isRequired,
  onChangeVariations: PropTypes.func.isRequired,
  stockManagementMessage: PropTypes.func.isRequired,
  packQuantity: PropTypes.number,
  onPackQuantityChange: PropTypes.func.isRequired,
  packVariations: PropTypes.arrayOf({
    variation: PropTypes.string,
    quantity: PropTypes.number,
  }),
};
HybridPackSection.defaultProps = {
  packQuantity: 0,
  packVariations: [],
};

function Section(props) {
  const {
    product,
    onChangeVariations,
    stockManagementMessage,
    packVariationsAvailable = [],
    name = '',
    amountVariationsToChoose = 1,
    packVariations: packVariationsProps = [],
    sectionId,
  } = props;

  const availableIds = new Set(packVariationsAvailable?.map(pA => pA._id));
  const packVariations = packVariationsProps.filter(
    p => availableIds.has(p._id) && p.sectionId === sectionId,
  );

  const { loggedUser } = useContext(LoggedUserContext);
  const [modal, showModal] = useModal({ show: false, data: null });

  const handleCloseModal = useCallback(
    products => {
      showModal({ show: false, data: null });
      if (products?.length > 0) {
        onChangeVariations([
          ...(packVariationsProps?.filter(p => p.sectionId !== sectionId) ||
            []),
          ...products.map(p => ({ ...p, sectionId })),
        ]);
      }
    },
    [showModal, onChangeVariations, packVariationsProps, sectionId],
  );

  const handleOpenModal = useCallback(
    action => {
      showModal({
        show: true,
        data: action === 'add' ? [] : packVariationsAvailable,
      });
    },
    [showModal, packVariationsAvailable],
  );

  const showSelectionOfProducts = packVariationsAvailable?.length > 0;

  const stock = useMemo(() => {
    const { packVariations: staticVariations = [] } = product;
    const totalVariations = [
      ...staticVariations.map(p => ({ ...p, ...p.variation })),
      ...packVariationsProps,
    ];

    return totalVariations.reduce((acc, item) => {
      const id = item.variation?._id || item._id;
      if (!acc[id]) {
        const stockQuantity =
          item.variation?.stockQuantity ?? item.stockQuantity;
        acc[id] = {
          quantity: item.quantity || 0,
          stockQuantity: stockQuantity ?? null,
        };
      } else {
        acc[id].quantity += item.quantity || 0;
      }
      return acc;
    }, {});
  }, [product, packVariationsProps]);

  return (
    <section className="advancedPack">
      {modal?.show && (
        <AddPackProduct
          product={{
            ...product,
            pack: {
              ...product.pack,
              amountVariationsPerAdvancedPack: amountVariationsToChoose,
            },
          }}
          user={loggedUser}
          onClose={handleCloseModal}
          selectedVariations={packVariations}
          packVariationsAvailable={packVariationsAvailable}
          stock={stock}
          amountVariationsToChoose={amountVariationsToChoose}
        />
      )}

      <h3>{name}</h3>

      {showSelectionOfProducts && (
        <AddEditSection
          isEditable={packVariations?.length > 0}
          pack={{
            ...product.pack,
            amountVariationsPerAdvancedPack: amountVariationsToChoose,
          }}
          openModal={handleOpenModal}
        />
      )}

      {packVariations.map(packVariation => (
        <PackVariation
          key={packVariation._id}
          packVariation={{
            ...packVariation,
            variation: packVariation,
          }}
          stockManagementMessage={stockManagementMessage}
          packQuantity={amountVariationsToChoose}
        />
      ))}
    </section>
  );
}

Section.propTypes = {
  product: PropTypes.shape(ProductProp.propType).isRequired,
  onChangeVariations: PropTypes.func.isRequired,
  stockManagementMessage: PropTypes.func.isRequired,
  packVariationsAvailable: PropTypes.arrayOf(
    PropTypes.shape(ProductVariationsProp.propType),
  ),
  amountVariationsToChoose: PropTypes.number,
  name: PropTypes.string,
  packVariations: PropTypes.arrayOf({
    variation: PropTypes.string,
    quantity: PropTypes.number,
  }),
  sectionId: PropTypes.number.isRequired,
};
Section.defaultProps = {
  packVariationsAvailable: [],
  amountVariationsToChoose: 1,
  name: '',
  packVariations: [],
};

export default translate(HybridPackSection);
